<template>
  <b-card style="position: relative">
    <div v-if="loading" class="loading_page">
      <orbit-spinner :animation-duration="1000" :size="100" :color="'#0e96b1'"/>
    </div>
    <filters 
      :categoriesFilter.sync="categoriesFilter"
      :contractsFilter.sync="contractsFilter"
      :ownersFilter.sync="ownersFilter"
      :typesFilter.sync="typesFilter"
    />
    <div class="m-2">
      <b-row>
        <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
          <label>{{ $t('Show') }}</label>
          <v-select v-model="perPage" :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="perPageOptions"
                    :clearable="false" class="per-page-selector d-inline-block mx-50"/>
          <label>{{ $t('entries') }}</label>
        </b-col>

        <b-col cols="12" md="6">
          <div class="d-flex align-items-center justify-content-end">
            <b-form-input v-model="macFilter" class="d-inline-block mr-1" :placeholder="$t('filter by mac address')"/>
            <b-form-input v-model="deviceFilter" class="d-inline-block mr-1" :placeholder="$t('filter by device')"/>
            <b-button v-if="($store.state.auth.userData.roles.includes('Super Admin') || $store.getters['auth/user'].permissions.includes('rfids.create'))" variant="primary"
                      :to="{ name: 'rfids.create' }">
              <span class="text-nowrap">{{ $t('Create Rfid')}}</span>
            </b-button>
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table ref="refTable" class="position-relative" :items="fetchRfids" responsive :fields="tableColumns"
        primary-key="id" show-empty empty-text="No matching records found">

      <template #cell(id)="data"> {{ data.index + 1 }} </template>

      <template #cell(actions)="data">
        <b-dropdown class="actionIcon" variant="link" no-caret :right="$store.state.appConfig.isRTL">
          <template #button-content>
            <feather-icon icon="MoreVerticalIcon" size="16" class="align-middle text-body"/>
          </template>
          <b-dropdown-item :to="{ name: 'rfids.edit', params: { id: data.item.id } }">
            <feather-icon icon="EditIcon" />
            <span class="align-middle ml-50">{{ $t('Edit') }}</span>
          </b-dropdown-item>
        </b-dropdown>
      </template>

    </b-table>

    <div class="mx-2 mb-2">
      <b-row>
        <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
          <span class="text-muted">{{ $t('Showing') }} {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.total }} {{$t('entries')}}</span>
        </b-col>
        <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
          <b-pagination v-model="currentPage" :total-rows="dataMeta.total"
              :per-page="perPage" first-number last-number
              class="mb-0 mt-1 mt-sm-0" prev-class="prev-item" next-class="next-item">
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18"/>
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18"/>
            </template>
          </b-pagination>

        </b-col>
      </b-row>
    </div>
  </b-card>
</template>

<script>

import { OrbitSpinner } from 'epic-spinners'
import {
  BButton, 
  BCard, 
  BCol, 
  BForm, 
  BFormGroup, 
  BFormInput,
  BInputGroup, 
  BRow, 
  BAlert, 
  BTabs, 
  BTab, 
  BCardText, 
  BTable, 
  BPagination, 
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { onUnmounted, ref, watch } from '@vue/composition-api'
import store from '@/store'
import RfidForm from '@/views/modules/rfid/components/Form'
import rfidStore from '@/app/rfid/rfidStoreModule'
import Filters from './components/Filters.vue'

export default {
  name: 'ListRfid',
  components: { 
    BAlert,
    BButton,
    BCard, 
    BCol,
    BFormGroup, 
    BFormInput, 
    BPagination,
    BRow, 
    BTable,
    BInputGroup, 
    BForm, 
    BTabs, 
    BCardText, 
    BTab, 
    OrbitSpinner, 
    BDropdown, 
    BDropdownItem, 
    Filters,
    RfidForm, 
    vSelect
  },
  directives: { Ripple },
  setup(props, { root }) {

    const RFID_STORE = 'rfid'
    store.registerModule(RFID_STORE, rfidStore)

    onUnmounted(() => {
      if (store.hasModule(RFID_STORE))
          store.unregisterModule(RFID_STORE)
    })

    const refTable = ref(null)
    const dataMeta = ref({})

    const tableColumns = [
      { key: 'id', sortable: true, label:root.$t('id')},
      { key: 'mac', sortable: true, label:root.$t('mac')}, 
      { key: 'device', sortable: true, label:root.$t('device')}   ,
      { key: 'lastRead', sortable: true, label:root.$t('last read')},
      { key: 'lastupdate', sortable: true, label:root.$t('lastupdate')},
      { key: 'actions', label: root.$t('actions') }
    ]
    const perPageOptions = [2,10,50,100,500]
    const perPage = ref(10)

    const categoriesFilter = ref(null)
    const contractsFilter = ref(null)
    const deviceFilter = ref(null)
    const macFilter = ref(null)
    const ownersFilter = ref(null)
    const typesFilter = ref(null)

    const searchQuery = ref('')
    const loading = ref(false)

    const currentPage = ref(1)
    const total = ref(0)

    const request = ref({})

    watch([perPage, searchQuery, currentPage, categoriesFilter, contractsFilter, deviceFilter, macFilter, ownersFilter, typesFilter], () => {
      refTable.value.refresh()
    })

    const fetchRfids = (ctx, callback) => {

      loading.value = true
      store.dispatch('rfid/listRfids', {
        length: perPage.value,
        page: currentPage.value,
        ...(categoriesFilter.value && {categories: categoriesFilter.value}),
        ...(contractsFilter.value && {contracts: contractsFilter.value}),
        ...(deviceFilter.value && {device: deviceFilter.value}),
        ...(macFilter.value && {mac: macFilter.value}),
        ...(ownersFilter.value && {owners: ownersFilter.value}),
        ...(typesFilter.value && {types: typesFilter.value}),
      }).then(r => {

        dataMeta.value = r.data.rfids.meta
        callback(r.data.rfids.data)
      }).catch(reason => {}).finally(() => loading.value = false)
    }


    return {
      categoriesFilter,
      contractsFilter,
      deviceFilter,
      macFilter,
      ownersFilter,
      typesFilter,
      refTable, 
      dataMeta, 
      total, 
      currentPage, 
      perPageOptions, 
      perPage,
      searchQuery, 
      fetchRfids, 
      tableColumns, 
      loading, 
      request
    }

  },

}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.actionIcon>button {
  padding: 0;
}
</style>
